import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
    Card, 
    Typography, 
    Button, 
    Modal, 
    Input, 
    message, 
    Result,
    Spin,
    Tooltip
} from 'antd';
import { 
    DownloadOutlined,
    FolderOpenOutlined,
    MailOutlined,
    RollbackOutlined,
    CloseOutlined,
    EditOutlined,
    EyeOutlined,
    CommentOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignature } from '@fortawesome/free-solid-svg-icons';
import { EmbedSignDocument } from '@documenso/embed-react';
import axios from '../../utility/axios';
import dayjs from 'dayjs';
import AttachmentPreview from '../../contexts/AttachmentPreview';
import AccessCodeModal from '../common/AccessCodeModal';
import '../../styles/documenso/DocumensoView.css';
import '../../styles/documents/DocumentPreview.css';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useBecomeGuest } from '../../contexts/BecomeGuestContext';
import { isConvertible } from '../../utility/fileTypes';

const { Title, Text } = Typography;

const DocumensoView = ({ mailboxHref }) => {
    const { t } = useTranslation();
    const { user, isLoggedIn } = useAuth();
    const { setCanBecomeGuest } = useBecomeGuest();
    const { unique_identifier } = useParams();
    const [messageData, setMessageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [requiresAccessCode, setRequiresAccessCode] = useState(false);
    const [viewerEmail, setViewerEmail] = useState(null);
    const [signModalOpen, setSignModalOpen] = useState(false);
    const [signModalFile, setSignModalFile] = useState(null);
    const [signModalToken, setSignModalToken] = useState(null);
    const [signModalHost, setSignModalHost] = useState(null);
    const [signModalLoading, setSignModalLoading] = useState(false);
    const [signModalError, setSignModalError] = useState(null);
    const [attachmentPreviewDoc, setAttachmentPreviewDoc] = useState(null);
    const [columnWidths, setColumnWidths] = useState(() => {
        const saved = localStorage.getItem('documenso_view_column_widths');
        return saved ? JSON.parse(saved) : { body: 60, files: 40 };
    });
    const [isResizing, setIsResizing] = useState(false);
    const contentRowRef = useRef(null);
    const FILES_COLUMN_MIN_PX = 320;
    const navigate = useNavigate();
    const location = useLocation();

    // Helper function to check if there are multiple recipients
    const hasMultipleRecipients = (recipients) => {
        if (!recipients) return false;
        // Check if there's a comma, semicolon or other separator in the recipients string
        if (typeof recipients === 'string') {
            // Split on common email separators (comma, semicolon, space)
            const emailList = recipients.split(/[,;\s]+/).map(email => email.trim()).filter(email => email !== '');
            return emailList.length > 1;
        }
        // If recipients is an array, check its length
        if (Array.isArray(recipients)) {
            return recipients.length > 1;
        }
        return false;
    };
    
    const fetchMessage = async () => {
        try {
            setLoading(true);
            const headers = {};
            
            // Check if we should preserve the view count
            let shouldPreserveViews = false;
            
            // First check if preserveViews is explicitly set in location state
            if (location.state?.preserveViews === true) {
                shouldPreserveViews = true;
                // Clear the state after using it to prevent preserving views on future navigations
                window.history.replaceState({}, document.title);
            } else if (location.state?.preserveViews === false) {
                // If preserveViews is explicitly set to false, we should NOT preserve views
                // This takes precedence over the session storage check
                shouldPreserveViews = false;
                // Clear the state after using it
                window.history.replaceState({}, document.title);
            } else {
                // Case 2: No explicit state - check if we've already viewed this message in this session
                const viewedMessagesStr = sessionStorage.getItem('viewedMessages') || '[]';
                const viewedMessages = JSON.parse(viewedMessagesStr);
                if (viewedMessages.includes(unique_identifier)) {
                    shouldPreserveViews = true;
                } else {
                    // Add this message to the list of viewed messages in this session
                    viewedMessages.push(unique_identifier);
                    sessionStorage.setItem('viewedMessages', JSON.stringify(viewedMessages));
                }
            }
            
            // Set header if we should preserve views
            if (shouldPreserveViews) {
                headers['X-Preserve-Views'] = 'true';
            }
            
            let endpoint = `/api/docuso/link/${unique_identifier}/`;
            const path = window.location.pathname;
            if (path.includes('/reply/')) {
                endpoint = `/api/docuso/link/reply/${unique_identifier}/`;
                
                // Check if this is a reply-all request
                if (window.location.search.includes('reply_all=true')) {
                    endpoint += '?reply_all=true';
                }
            }
            
            const response = await axios.get(endpoint, {
                headers
            });
            
            setMessageData(response.data.message);
            setViewerEmail(response.data.viewer_email);
            setError(null);
            setRequiresAccessCode(false);
        } catch (err) {
            if (err.response?.status === 401 && err.response?.data?.requires_verification) {
                setRequiresAccessCode(true);
                setError(null);
            } else if (err.response?.status === 410) {
                setError(t('messages.view.errorExpired'));
            } else if (err.response?.status === 404) {
                setError(t('messages.view.errorNotFound'));
            } else {
                setError(t('messages.view.errorFailedToLoad'));
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMessage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unique_identifier]);

    const handleAccessCodeSuccess = () => {
        setRequiresAccessCode(false);
        setCanBecomeGuest?.(true);
        fetchMessage();
    };

    // Handle column resize
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing || !contentRowRef.current) return;
            const containerRect = contentRowRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            const mousePercent = (mouseX / containerWidth) * 100;
            let bodyWidth = Math.max(30, Math.min(80, mousePercent));
            let filesWidth = 100 - bodyWidth;
            const filesMinPercent = (FILES_COLUMN_MIN_PX / containerWidth) * 100;
            if (filesWidth < filesMinPercent) {
                filesWidth = filesMinPercent;
                bodyWidth = 100 - filesWidth;
            }
            const newWidths = { body: bodyWidth, files: filesWidth };
            setColumnWidths(newWidths);
            localStorage.setItem('documenso_view_column_widths', JSON.stringify(newWidths));
        };
        const handleMouseUp = () => {
            if (isResizing) {
                setIsResizing(false);
                document.body.classList.remove('docpreview-resizing');
            }
        };
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('docpreview-resizing');
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('docpreview-resizing');
        };
    }, [isResizing]);

    const handleDocumensoFileClick = async (file) => {
        const envelopeId = file?.envelope_id;
        if (viewerEmail && envelopeId) {
            setSignModalOpen(true);
            setSignModalFile(file);
            setSignModalToken(null);
            setSignModalHost(null);
            setSignModalError(null);
            setSignModalLoading(true);
            try {
                const res = await axios.get(
                    `/api/docuso/signing-token/?link_id=${unique_identifier}&envelope_id=${encodeURIComponent(envelopeId)}`
                );
                setSignModalToken(res.data.token);
                setSignModalHost(res.data.host || null);
                setSignModalError(null);
            } catch (err) {
                const status = err.response?.status;
                const errMsg = err.response?.data?.error || t('messages.view.errorFailedDownload');
                const detail = status ? `${errMsg} (${status})` : errMsg;
                setSignModalError(detail);
                setSignModalToken(null);
                setSignModalHost(null);
            } finally {
                setSignModalLoading(false);
            }
        } else {
            handleDownload(file.unique_identifier, file.file_name || file.pdf_name, true);
        }
    };

    const handleDownload = async (attachmentId, fileName, isDocumenso = false) => {
        try {
            const downloadUrl = isDocumenso
                ? `/api/docuso/link/${unique_identifier}/download/${attachmentId}/`
                : `/api/messages/download/${attachmentId}/`;
            const response = await axios.get(downloadUrl, {
                responseType: 'blob'
            });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            message.error(t('messages.view.errorFailedDownload'));
        }
    };

    const handleDownloadAll = async () => {
        try {
            const hasDocumenso = messageData?.message_type === 'documenso' || (messageData?.documenso_files?.length > 0);
            const downloadUrl = hasDocumenso
                ? `/api/docuso/link/${unique_identifier}/download-all/`
                : `/api/messages/${messageData.unique_identifier}/download-all/`;
            const response = await axios.get(downloadUrl, {
                responseType: 'blob'
            });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', 'attachments.zip');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            message.error(t('messages.view.errorFailedDownload'));
        }
    };

    const handleReply = () => {
        // Navigate to the reply URL with state to preserve views
        const replyUrl = `/link/reply/${unique_identifier}?action=reply`;
        navigate(replyUrl, { state: { preserveViews: true } });
    };

    const handleReplyAll = () => {
        // Navigate to the reply URL with reply_all flag and state to preserve views
        const replyAllUrl = `/link/reply/${unique_identifier}?reply_all=true&action=reply_all`;
        navigate(replyAllUrl, { state: { preserveViews: true } });
    };

    // Check if current viewer is only in BCC and not in regular recipients or CC
    const isViewerOnlyInBcc = () => {
        if (!messageData || !viewerEmail) return false;
        
        // Check if viewer is in BCC
        const bccList = messageData.bcc ? messageData.bcc.split(/[,;\s]+/).map(email => email.trim().toLowerCase()) : [];
        const isInBcc = bccList.includes(viewerEmail.toLowerCase());
        
        // Check if viewer is also in regular recipients or CC (in which case Reply All should be available)
        const recipientList = messageData.recipient ? messageData.recipient.split(/[,;\s]+/).map(email => email.trim().toLowerCase()) : [];
        const ccList = messageData.cc ? messageData.cc.split(/[,;\s]+/).map(email => email.trim().toLowerCase()) : [];
        
        const isInRecipientOrCc = recipientList.includes(viewerEmail.toLowerCase()) || ccList.includes(viewerEmail.toLowerCase());
        
        // Return true only if viewer is in BCC but not in regular recipients or CC
        return isInBcc && !isInRecipientOrCc;
    };

    if (loading) {
        return (
            <div className="documenso-view-loading">
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="documenso-view-error-container">
                <Card className="documenso-view-error-card">
                    <Result
                        status="error"
                        title={error}
                        subTitle={t('messages.view.errorSubtitle')}
                        extra={[
                            <Button 
                                key="close"
                                onClick={() => navigate(user ? '/profile/mailbox' : '/mailbox')}
                                icon={<CloseOutlined />}
                            >
                                {t('messages.view.close')}
                            </Button>
                        ]}
                    />
                </Card>
            </div>
        );
    }

    return (
        <div className="documenso-view-container">
            {/* Back to Mailbox Header */}
            <div className="documenso-view-top-header">
                {mailboxHref && (
                    <Button
                        type="text"
                        icon={<RollbackOutlined />}
                        onClick={() => navigate(mailboxHref)}
                        className="documenso-view-back-to-mailbox-btn"
                    >
                        {t('messages.view.backToMailbox')}
                    </Button>
                )}
                <div className="documenso-view-action-buttons">
                    {isLoggedIn && (messageData?.cc || hasMultipleRecipients(messageData?.recipient)) && !isViewerOnlyInBcc() && (
                        <Button 
                            icon={<RollbackOutlined />}
                            onClick={handleReplyAll}
                            className="documenso-view-action-btn"
                        >
                            {t('messages.view.replyAll')}
                        </Button>
                    )}
                    <Button 
                        icon={<RollbackOutlined />}
                        onClick={handleReply}
                        className="documenso-view-action-btn"
                    >
                        {t('messages.view.reply')}
                    </Button>
                </div>
            </div>

            <div className="documenso-view-detail-body-wrapper">
                {messageData?.self_destruct && (
                    <div className="documenso-view-self-destruct-warning">
                        {t('messages.view.selfDestructWarning')}
                    </div>
                )}
                {messageData && (
                    <>
                        {/* Email Info Section */}
                        <div 
                            className={`documenso-view-email-info ${messageData.prevent_message_copy ? 'no-copy' : ''}`}
                            onCopy={(e) => {
                                if (messageData.prevent_message_copy) {
                                    e.preventDefault();
                                    message.warning(t('messages.view.copyingDisabled'));
                                    return false;
                                }
                            }}
                            onContextMenu={(e) => {
                                if (messageData.prevent_message_copy) {
                                    e.preventDefault();
                                    return false;
                                }
                            }}
                        >
                            <div className="documenso-view-icon-wrapper">
                                <MailOutlined />
                            </div>
                            <div className="documenso-view-email-details">
                                <div className="documenso-view-sender-receiver">
                                    <div className="documenso-view-email-row">
                                        <Text strong>{t('messages.view.from')} </Text>
                                        <Text>{messageData.sender}</Text>
                                    </div>
                                    <div className="documenso-view-email-row">
                                        <Text strong>{t('messages.view.to')} </Text>
                                        <Text>{messageData.recipient}</Text>
                                    </div>
                                </div>
                                {messageData.cc && (
                                    <div className="documenso-view-cc-section">
                                        <div className="documenso-view-email-row">
                                            <Text strong>{t('messages.view.cc')} </Text>
                                            <Text>{messageData.cc}</Text>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Metadata on the right side */}
                            <div className="documenso-view-metadata-side">
                                {messageData.self_destruct && (
                                    <div className="documenso-view-metadata-item">
                                        <div className="documenso-view-metadata-label">{t('messages.view.selfDestruct')}</div>
                                        <div className="documenso-view-metadata-value">
                                            {t('messages.view.enabled')}
                                        </div>
                                    </div>
                                )}
                                <div className="documenso-view-metadata-item">
                                    <div className="documenso-view-metadata-label">{t('messages.view.expires')}</div>
                                    <div className="documenso-view-metadata-value">
                                        {dayjs(messageData.timestamp).format('MMM D, YYYY, h:mm A')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Subject Section */}
                        <div 
                            className={`documenso-view-subject-section ${messageData.prevent_message_copy ? 'no-copy' : ''}`}
                            onCopy={(e) => {
                                if (messageData.prevent_message_copy) {
                                    e.preventDefault();
                                    message.warning(t('messages.view.copyingDisabled'));
                                    return false;
                                }
                            }}
                            onContextMenu={(e) => {
                                if (messageData.prevent_message_copy) {
                                    e.preventDefault();
                                    return false;
                                }
                            }}
                        >
                            <Title level={3} className="documenso-view-subject-title">
                                {messageData.subject}
                            </Title>
                        </div>

                        {/* Two-column layout when documenso files exist */}
                        <div className="document-preview-content-row" ref={contentRowRef}>
                            {/* Body Column - Left */}
                            <div 
                                className="document-preview-body-column"
                                style={{ width: (messageData?.documenso_files?.length > 0) ? `${columnWidths.body}%` : '100%' }}
                            >
                                {/* Regular attachments */}
                                {(messageData?.attachments?.length > 0) && (
                                    <div className="documenso-view-attachments-section">
                                        <div className="documenso-view-attachments-list">
                                            {(messageData?.attachments || []).map((attachment, index) => (
                                                <div
                                                    key={attachment.unique_identifier || index}
                                                    className="documenso-view-attachment-item"
                                                    onClick={() => handleDownload(attachment.unique_identifier, attachment.file_name, false)}
                                                >
                                                    <div className="documenso-view-attachment-filename">{attachment.file_name}</div>
                                                    <div className="documenso-view-attachment-filesize">
                                                        {typeof attachment.file_size === 'number'
                                                            ? `${(attachment.file_size / 1024).toFixed(2)} KB`
                                                            : attachment.file_size
                                                        }
                                                    </div>
                                                    <DownloadOutlined className="documenso-view-attachment-download-icon" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {/* Message Body */}
                                <div 
                                    className={`documenso-view-body-section document-preview-body-section ${messageData.prevent_message_copy ? 'no-copy' : ''}`}
                                    onCopy={(e) => {
                                        if (messageData.prevent_message_copy) {
                                            e.preventDefault();
                                            message.warning(t('messages.view.copyingDisabled'));
                                            return false;
                                        }
                                    }}
                                    onContextMenu={(e) => {
                                        if (messageData.prevent_message_copy) {
                                            e.preventDefault();
                                            return false;
                                        }
                                    }}
                                >
                                    <div 
                                        className="documenso-view-body-content document-body-content"
                                        dangerouslySetInnerHTML={{ __html: messageData.body }}
                                    />
                                </div>
                            </div>

                            {/* Resize Handle */}
                            {(messageData?.documenso_files?.length > 0) && (
                                <div 
                                    className="docpreview-resize-handle"
                                    onMouseDown={(e) => { e.preventDefault(); setIsResizing(true); }}
                                />
                            )}

                            {/* Documenso Files - Right Column */}
                            {(messageData?.documenso_files?.length > 0) && (
                                <div 
                                    className="document-preview-files-column documenso-view-files-column"
                                    style={{ width: `${columnWidths.files}%`, minWidth: FILES_COLUMN_MIN_PX }}
                                >
                                    <div className="docpreview-files-list-section">
                                        <div className="docpreview-files-section-header">
                                            <span className="docpreview-files-section-label">
                                                {t('documensoPreview.signatureDocuments') || 'Signature documents'}
                                            </span>
                                            <div className="docpreview-files-section-header-right">
                                                <span className="docpreview-files-section-counter">
                                                    {messageData.documenso_files.length} {t('documensoPreview.files') || 'file(s)'}
                                                </span>
                                                {(messageData.documenso_files || []).some(f => viewerEmail && f.envelope_id) && (
                                                    <Tooltip title={t('documenso.signDocument', 'Sign document')}>
                                                        <Button
                                                            type="primary"
                                                            size="small"
                                                            icon={<FontAwesomeIcon icon={faSignature} />}
                                                            onClick={() => {
                                                                const firstSignable = (messageData.documenso_files || []).find(f => viewerEmail && f.envelope_id);
                                                                if (firstSignable) handleDocumensoFileClick(firstSignable);
                                                            }}
                                                            className="documenso-view-main-sign-btn"
                                                        >
                                                            {t('documenso.signDocument', 'Sign document')}
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                                {messageData.documenso_files.filter(f => f.file_name || f.pdf_name).length >= 1 && (
                                                    <Tooltip title={t('messages.view.downloadAll')}>
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={<FolderOpenOutlined />}
                                                            onClick={handleDownloadAll}
                                                            className="docpreview-files-download-all-btn"
                                                        />
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </div>
                                        <div className="docpreview-files-list">
                                            {(messageData?.documenso_files || []).map((file, index) => {
                                                const fileName = file.file_name || file.pdf_name;
                                                const fileSize = typeof file.file_size === 'number'
                                                    ? `${(file.file_size / 1024).toFixed(2)} KB`
                                                    : file.file_size;
                                                const canSign = viewerEmail && file.envelope_id;
                                                const hasFile = !!fileName;
                                                const canPreview = hasFile && isConvertible(fileName);
                                                const commentCount = file.comment_count ?? (file.comments?.length ?? 0);
                                                const openAttachmentPreview = () => {
                                                    if (canPreview) setAttachmentPreviewDoc({ documentFileId: file.unique_identifier, fileName });
                                                };
                                                return (
                                                    <div key={file.unique_identifier || index} className="docpreview-file-row-wrapper">
                                                        <div
                                                            className={`docpreview-file-row docpreview-file-row-documenso-view ${canPreview ? 'docpreview-file-row-previewable' : ''} ${canPreview && commentCount > 0 ? 'docpreview-file-row-has-comments' : ''}`}
                                                            onClick={canPreview ? (e) => { if (!e.target.closest('.docpreview-file-actions')) openAttachmentPreview(); } : undefined}
                                                            role={canPreview ? 'button' : undefined}
                                                            tabIndex={canPreview ? 0 : undefined}
                                                            onKeyDown={canPreview ? (e) => { if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('.docpreview-file-actions')) { e.preventDefault(); openAttachmentPreview(); } } : undefined}
                                                        >
                                                            {canPreview && commentCount > 0 && (
                                                                <span
                                                                    className="docpreview-file-comment-badge"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setAttachmentPreviewDoc({ documentFileId: file.unique_identifier, fileName });
                                                                    }}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onKeyDown={(e) => {
                                                                        if ((e.key === 'Enter' || e.key === ' ') && commentCount > 0) {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            setAttachmentPreviewDoc({ documentFileId: file.unique_identifier, fileName });
                                                                        }
                                                                    }}
                                                                    aria-label={`${commentCount} comment${commentCount !== 1 ? 's' : ''}`}
                                                                >
                                                                    <CommentOutlined />
                                                                    <span className="docpreview-file-comment-badge-count">{commentCount}</span>
                                                                </span>
                                                            )}
                                                            <div className="docpreview-file-title-cell">
                                                                <span className="docpreview-file-title documenso-view-file-title">
                                                                    {fileName || (t('documensoPreview.documentPending') || 'Document pending')}
                                                                </span>
                                                                {fileSize && <span className="docpreview-file-size">{fileSize}</span>}
                                                            </div>
                                                            <div className="docpreview-file-actions documenso-view-file-actions">
                                                                <div className="docpreview-file-preview">
                                                                    {canPreview ? (
                                                                        <Button
                                                                            type="text"
                                                                            size="small"
                                                                            icon={<EyeOutlined />}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setAttachmentPreviewDoc({ documentFileId: file.unique_identifier, fileName });
                                                                            }}
                                                                            className="docpreview-file-action-btn docpreview-file-preview-btn"
                                                                        />
                                                                    ) : (
                                                                        <span className="docpreview-file-action-disabled"><EyeOutlined /></span>
                                                                    )}
                                                                </div>
                                                                <div className="docpreview-file-download">
                                                                    <Button
                                                                        type="text"
                                                                        size="small"
                                                                        icon={<DownloadOutlined />}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDownload(file.unique_identifier, fileName, true);
                                                                        }}
                                                                        className="docpreview-file-action-btn docpreview-file-download-btn"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            <Modal
                title={signModalFile ? (t('documenso.signDocument', 'Sign document') + ': ' + signModalFile.file_name) : (t('documenso.signDocument', 'Sign document'))}
                open={signModalOpen}
                onCancel={() => { setSignModalOpen(false); setSignModalToken(null); setSignModalHost(null); setSignModalError(null); setSignModalFile(null); }}
                footer={null}
                width="95vw"
                style={{ maxWidth: 1400 }}
                bodyStyle={{ height: '70vh', minHeight: 400, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                destroyOnClose
            >
                {signModalLoading && (
                    <div className="documenso-view-sign-modal-loading">
                        <Spin size="large" />
                    </div>
                )}
                {signModalError && !signModalLoading && (
                    <div className="documenso-view-sign-modal-loading">
                        <Result
                        status="error"
                        title={signModalError}
                        extra={
                            <Button type="primary" onClick={() => setSignModalOpen(false)}>
                                {t('messages.view.close', 'Close')}
                            </Button>
                        }
                    />
                    </div>
                )}
                {signModalToken && !signModalLoading && (
                    <div className="documenso-view-sign-embed-container">
                        <EmbedSignDocument
                            token={signModalToken}
                            {...(signModalHost && { host: signModalHost })}
                            onDocumentCompleted={() => {
                                message.success(t('documenso.signingComplete', 'Document signed successfully'));
                                setSignModalOpen(false);
                                setSignModalToken(null);
                                setSignModalHost(null);
                                setSignModalFile(null);
                            }}
                            onDocumentError={(err) => {
                                const errMsg = err?.message || err?.toString?.() || t('documenso.signingError', 'An error occurred during signing');
                                message.error(errMsg);
                                console.error('[DocumensoView] Embed error:', err);
                            }}
                        />
                    </div>
                )}
            </Modal>

            <AttachmentPreview
                open={!!attachmentPreviewDoc}
                onClose={() => setAttachmentPreviewDoc(null)}
                documentFileId={attachmentPreviewDoc?.documentFileId}
                fileName={attachmentPreviewDoc?.fileName}
                source="documenso"
                linkId={unique_identifier}
                onDownload={attachmentPreviewDoc ? () => handleDownload(attachmentPreviewDoc.documentFileId, attachmentPreviewDoc.fileName, true) : undefined}
            />

            <AccessCodeModal
                open={requiresAccessCode}
                rid={unique_identifier}
                onSuccess={handleAccessCodeSuccess}
                onCancel={() => {
                    setRequiresAccessCode(false);
                    setError(t('documenso.errorAccessCodeRequired') || 'Access code required to view this document');
                }}
            />
        </div>
    );
};

export default DocumensoView;